/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { Theme, Typography2Props } from '@coursera/cds-core';
import { Typography2, useTheme } from '@coursera/cds-core';
import { AnswerCorrectIcon, AnswerWrongIcon } from '@coursera/cds-icons';

import { CMLOrHTML } from 'bundles/cml';
import cmlObjectContainsValue from 'bundles/compound-assessments/lib/utils/cmlObjectContainsValue';
import type { QuestionGradeFeedbackInfo } from 'bundles/compound-assessments/lib/utils/getQuestionGradeFeedbackInfo';
import { GradeFeedbackStrings } from 'bundles/compound-assessments/lib/utils/getQuestionGradeFeedbackInfo';
import type { CMLOrHTMLContent } from 'bundles/compound-assessments/types/FormParts';

const styles = {
  root: (theme: Theme, type: string, renderGrayIncorrect: boolean) => {
    let bgColor = '';
    switch (type) {
      case GradeFeedbackStrings.SUCCESS:
      case GradeFeedbackStrings.REFLECTIVE_FEEDBACK:
        bgColor = 'var(--cds-color-green-50)';
        break;
      case GradeFeedbackStrings.FAILURE:
        bgColor = renderGrayIncorrect ? 'var(--cds-color-grey-25)' : 'var(--cds-color-red-50)';
        break;
      default:
        bgColor = 'var(--cds-color-grey-25)';
    }
    return css({
      marginTop: '16px',
      padding: '12px',
      display: 'flex',
      backgroundColor: bgColor,
    });
  },
  iconContainer: css({
    marginRight: '8px',
    height: '20px',
  }),
  infoContainer: css({
    width: '100%',
  }),
  info: (type: string) => css({ marginLeft: type === GradeFeedbackStrings.PARTIAL ? '20px' : 0 }),
};

export const FeedbackContent = ({
  description,
  questionType,
}: {
  description?: string | CMLOrHTMLContent | null;
  questionType?: string;
}) => {
  if (description && cmlObjectContainsValue(description)) {
    const content = Object.prototype.hasOwnProperty.call(description, 'typeName') ? (
      <CMLOrHTML value={description} display="inline-block" />
    ) : (
      // @ts-expect-error TS2322: Type `string | CMLOrHTMLContent` is not assignable to type `ReactNode`
      <span>{description}</span>
    );
    return questionType === 'codeExpression' ? <pre>{content}</pre> : content;
  }
  return null;
};

type Props = QuestionGradeFeedbackInfo & {
  questionType?: string;
  renderGrayIncorrect?: boolean;
};

// TODO refine display to present reflective feedback as "Feedback" instead of "Correct"
// https://coursera.atlassian.net/browse/CP-4419
const GradeFeedback: React.FC<Props> = ({ type, notificationInfo, questionType, renderGrayIncorrect = false }) => {
  const theme = useTheme();
  const typeToIcon = {
    [GradeFeedbackStrings.SUCCESS]: <AnswerCorrectIcon color="success" />,
    [GradeFeedbackStrings.FAILURE]: <AnswerWrongIcon color="error" />,
  };
  // @ts-expect-error ts-migrate(7053) FIXME: Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
  const icon = typeToIcon[type] || null;

  let labelColor: Typography2Props['color'] = 'body';
  if (type === GradeFeedbackStrings.SUCCESS) {
    labelColor = 'success';
  } else if (type === GradeFeedbackStrings.FAILURE) {
    labelColor = 'error';
  } else if (type === GradeFeedbackStrings.REFLECTIVE_FEEDBACK) {
    labelColor = 'body';
  }

  return (
    <div css={styles.root(theme, type, renderGrayIncorrect)} data-testid={`GradeFeedback-${type}`}>
      {icon && (
        <div css={styles.iconContainer} data-testid="GradeFeedback-icon">
          {icon}
        </div>
      )}
      {notificationInfo && (
        <div css={styles.infoContainer}>
          {notificationInfo.map(({ label, description }, index) => (
            <div css={styles.info(type)} key={label || `notificationKey-${index}`} data-testid="GradeFeedback-info">
              {label && (
                <Typography2
                  color={labelColor}
                  variant="subtitleMedium"
                  component="div"
                  data-testid="GradeFeedback-caption"
                >
                  {label}
                </Typography2>
              )}
              {description && (
                <Typography2 variant="bodySecondary" component="div">
                  <FeedbackContent description={description} questionType={questionType} />
                </Typography2>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default GradeFeedback;
